// Shared last-name sorting.
//
// The surname of a student with a compound name like "Bryan Balmaceda Ortega"
// is EVERYTHING after the first name ("Balmaceda Ortega" → sorts under B), not
// just the final word ("Ortega" → wrongly sorts under O). Several lists used to
// re-derive the surname as `split(" ").slice(-1)[0]` (the last word), which put
// those students under the wrong letter. This helper is the one source of truth.
//
// We prefer the stored `lastName` field — set at add/import time as the full
// remainder after the first name, and correct for both "First Last1 Last2" and
// "Last1 Last2, First" roster formats — and only fall back to deriving it from
// the full name when the field is missing.

// Accepts a student object ({ lastName, studentName, firstName, name }) or a
// bare full-name string. Returns a lowercase key for comparison.
export function surnameKey(studentOrName) {
  if (studentOrName && typeof studentOrName === "object") {
    const s = studentOrName;
    const field = (s.lastName || "").trim();
    if (field) return field.toLowerCase();
    const full = s.studentName || s.name || "";
    return afterFirstWord(full);
  }
  return afterFirstWord(studentOrName || "");
}

// Given-name key, used to break surname ties.
export function givenKey(studentOrName) {
  if (studentOrName && typeof studentOrName === "object") {
    const s = studentOrName;
    const field = (s.firstName || "").trim();
    if (field) return field.toLowerCase();
    return firstWord(s.studentName || s.name || "");
  }
  return firstWord(studentOrName || "");
}

// Comparator: sort by surname, then given name, case-insensitively.
export function byLastName(a, b) {
  const s = surnameKey(a).localeCompare(surnameKey(b), undefined, { sensitivity: "base" });
  if (s !== 0) return s;
  return givenKey(a).localeCompare(givenKey(b), undefined, { sensitivity: "base" });
}

// Kid-facing DISPLAY name: if the student has a nickname (they go by a different
// name), show "<nickname> <lastName>" on the sign-out tiles / rosters. Records and
// accountability surfaces (Emergency, history) keep the legal `studentName`, so a
// nickname never hides who a kid actually is. Falls back to the stored full name.
export function displayName(student) {
  if (!student) return "";
  const nick = (student.nickname || "").trim();
  if (nick) {
    const last = (student.lastName || "").trim();
    return last ? `${nick} ${last}` : nick;
  }
  return student.studentName || `${student.firstName || ""} ${student.lastName || ""}`.trim();
}

function afterFirstWord(name) {
  return (name || "").trim().split(/\s+/).slice(1).join(" ").toLowerCase();
}

function firstWord(name) {
  return (name || "").trim().split(/\s+/)[0]?.toLowerCase() || "";
}
